import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { divertilandiaColors } from '../theme/divertilandiaColors';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPWAProps {
  variant?: 'button' | 'icon';
  showOnDesktop?: boolean;
}

const InstallPWA: React.FC<InstallPWAProps> = ({ 
  variant = 'icon',
  showOnDesktop = false 
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'error'>('info');

  useEffect(() => {
    // Verificar si ya está instalada la PWA
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone === true;
    
    console.log('PWA Install Button - Estado:', {
      isInstalled,
      userAgent: navigator.userAgent,
      showOnDesktop
    });
    
    if (isInstalled) {
      console.log('PWA ya está instalada, ocultando botón');
      return; // No mostrar el botón si ya está instalada
    }

    // Detectar si es dispositivo móvil
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    console.log('Dispositivo móvil detectado:', isMobile);
    
    // Solo mostrar en móvil a menos que showOnDesktop sea true
    if (!isMobile && !showOnDesktop) {
      console.log('No es móvil y showOnDesktop es false, ocultando botón');
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('Evento beforeinstallprompt recibido!');
      // Prevenir que el navegador muestre su propio prompt
      e.preventDefault();
      // Guardar el evento para usarlo después
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Mostrar el botón de instalación
      setShowInstallButton(true);
      console.log('Botón de instalación activado');
    };

    // Escuchar el evento beforeinstallprompt
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Escuchar cuando la app se instala
    window.addEventListener('appinstalled', () => {
      setShowInstallButton(false);
      setSnackbarMessage('¡Aplicación instalada correctamente! 🎉');
      setSnackbarSeverity('success');
      setShowSnackbar(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [showOnDesktop]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Mostrar el prompt de instalación
    deferredPrompt.prompt();

    // Esperar a que el usuario responda al prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setSnackbarMessage('¡Instalando aplicación...!');
      setSnackbarSeverity('info');
      setShowSnackbar(true);
    } else {
      setSnackbarMessage('Instalación cancelada');
      setSnackbarSeverity('info');
      setShowSnackbar(true);
    }

    // Limpiar el prompt
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  if (!showInstallButton) {
    return null;
  }

  return (
    <>
      {variant === 'button' ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<InstallIcon />}
          onClick={handleInstallClick}
          sx={{
            background: `linear-gradient(135deg, ${divertilandiaColors.rainbow.blue} 0%, ${divertilandiaColors.rainbow.green} 100%)`,
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 0.75 },
            '&:hover': {
              background: `linear-gradient(135deg, ${divertilandiaColors.rainbow.green} 0%, ${divertilandiaColors.rainbow.blue} 100%)`,
            },
            boxShadow: 2,
            whiteSpace: 'nowrap',
          }}
        >
          Instalar App
        </Button>
      ) : (
        <Tooltip title="Instalar aplicación">
          <IconButton
            color="inherit"
            onClick={handleInstallClick}
            sx={{
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <InstallIcon />
          </IconButton>
        </Tooltip>
      )}

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstallPWA;

